import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, map, tap } from 'rxjs';
import { environment } from '../../environments/environment';

const TENANT_KEY = 'zf.selectedTenant';

/** Workspace a SUPER_ADMIN has entered from Super Admin > Workspaces. */
export interface SelectedTenant {
  id: string;
  name: string;
  code?: string;
  /** True when the workspace is an industry template being edited. */
  isTemplate?: boolean;
}

@Injectable({ providedIn: 'root' })
export class AuthService {
  private tokenKey = 'authToken';
  private api = environment.apiUrl;
  isLoggedIn$ = new BehaviorSubject<boolean>(
    !!localStorage.getItem(this.tokenKey)
  );
  private locationSource = new BehaviorSubject<string | null>(
    localStorage.getItem('PrimaryLocationName')
  );
  location$ = this.locationSource.asObservable();
  private userSource = new BehaviorSubject<any>(this.getStoredUser());
  user$ = this.userSource.asObservable();
  isRestrictedClient$: Observable<boolean> = this.user$.pipe(
    map((user) => user?.clientType === 'Clinic') // Check if clientType is 'Clinic'
  );
  logoUrl$: Observable<string | null> = this.user$.pipe(
    map((user) => user?.preferences?.logoUrl || null)
  );
  /**
   * Workspace a SUPER_ADMIN is working in. Sent as `x-tenant-id` on every
   * request (AuthInterceptor); null = platform level (Super Admin screens).
   */
  private selectedTenantSource = new BehaviorSubject<SelectedTenant | null>(
    AuthService.readSelectedTenant()
  );
  selectedTenant$ = this.selectedTenantSource.asObservable();

  constructor(private http: HttpClient) {
    // Initialize role from stored user data on app startup
    this.initializeFromStorage();
  }

  private initializeFromStorage(): void {
    const storedUser = this.getStoredUser();
    if (storedUser && storedUser.roles && storedUser.roles.length > 0) {
      // Set the role in localStorage if it exists in user data
      const primaryRole = storedUser.roles[0]?.role?.name || storedUser.roles[0]?.roleId;
      if (primaryRole && !localStorage.getItem('role')) {
        localStorage.setItem('role', primaryRole);
      }
    }
  }
  private loading = new BehaviorSubject<boolean>(false);
  loading$ = this.loading.asObservable();

  show() {
    this.loading.next(true);
  }

  hide() {
    this.loading.next(false);
  }
  login(credentials: { identifier: string; secret: string }) {
    return this.http
      .post<{ access_token: string }>(`${this.api}/auth/login`, credentials)
      .pipe(
        tap((res) => {
          localStorage.setItem(this.tokenKey, res.access_token);
          this.isLoggedIn$.next(true);
          // ✅ Fetch and store user data on successful login
          this.getLoggedInUser().subscribe((user: any) => {
            localStorage.setItem('user', JSON.stringify(user));
            this.userSource.next(user);

            // ✅ Store the primary role
            if (user.roles && user.roles.length > 0) {
              const primaryRole = user.roles[0]?.role?.name || user.roles[0]?.roleId;
              if (primaryRole) {
                localStorage.setItem('role', primaryRole);
              }
            }

            // Multi-store users pick their store explicitly (select-store
            // screen); only auto-bind the location when there is no choice
            // to make.
            const access = user.storeAccess ?? [];
            if (access.length === 1) {
              this.setActiveStore(access[0]);
            } else if (access.length === 0 && user.primaryLocationId) {
              this.fetchAndStoreLocation(user.primaryLocationId);
            }
          });
        })
      );
  }

  /** Store/warehouse the user chose to work in for this session. */
  setActiveStore(access: {
    storeId?: string | null;
    locationId: string;
    locationName?: string | null;
  }) {
    localStorage.setItem('activeLocationId', access.locationId);
    if (access.storeId) localStorage.setItem('storeId', access.storeId);
    else localStorage.removeItem('storeId');
    if (access.locationName) {
      localStorage.setItem('PrimaryLocationName', access.locationName);
      this.locationSource.next(access.locationName);
    } else {
      this.fetchAndStoreLocation(access.locationId);
    }
  }

  /** Active location: explicit choice first, then the backend's default. */
  getActiveLocationId(): string | null {
    return (
      localStorage.getItem('activeLocationId') ||
      this.getStoredUser()?.primaryLocationId ||
      null
    );
  }

  /** Stores/warehouses this user may work in (from /auth/me). */
  getStoreAccess(): Array<{
    storeId: string;
    storeCode?: string;
    storeName?: string;
    locationId: string;
    locationName?: string;
    locationType?: string;
  }> {
    return this.getStoredUser()?.storeAccess ?? [];
  }
  private fetchAndStoreLocation(locationId: string) {
    this.http
      .get<{ id: string; name: string }>(`${this.api}/locations/${locationId}`)
      .subscribe({
        next: (location) => {
          localStorage.setItem('PrimaryLocationName', location.name);
          this.locationSource.next(location.name); // <-- notify HeaderComponent
        },
        error: (err) => console.error('Failed to fetch location', err),
      });
  }

  getToken(): string | null {
    return localStorage.getItem(this.tokenKey);
  }

  isLoggedIn(): boolean {
    return !!localStorage.getItem('authToken');
  }

  logout() {
    localStorage.removeItem(this.tokenKey);
    localStorage.removeItem('role');
    localStorage.removeItem('user');
    localStorage.removeItem('storeId');
    localStorage.removeItem('activeLocationId');
    localStorage.removeItem('PrimaryLocationName');
    localStorage.removeItem(TENANT_KEY);
    this.selectedTenantSource.next(null);
    // The default-password banner is dismissed per session; a different user
    // signing in on this tab must see their own prompt.
    sessionStorage.removeItem('pwdPromptDismissed');
    this.isLoggedIn$.next(false);
    this.userSource.next(null);

    this.locationSource.next(null);
  }

  getLoggedInUser() {
    return this.http.get(`${this.api}/auth/me`);
  }

  /**
   * Update the signed-in user's own account (PATCH /auth/me). The backend
   * takes the target user from the token, and only accepts name / email /
   * mobile / pin / password — username, roles and store access are not
   * editable here.
   */
  updateMyProfile(payload: {
    name?: string;
    email?: string;
    mobile?: string;
    currentPassword?: string;
    newPassword?: string;
    pin?: string;
  }) {
    return this.http.patch(`${this.api}/auth/me`, payload);
  }

  /** Replace the cached user after a self-service edit so the header and
   *  every user$ subscriber show the new details without a re-login. */
  refreshStoredUser(user: any): void {
    if (!user) return;
    localStorage.setItem('user', JSON.stringify(user));
    this.userSource.next(user);
  }

  getRole(): string {
    return localStorage.getItem('role') || '';
  }

  /** Every role name the user holds (UserRole is many-to-many). */
  getRoles(): string[] {
    const stored = this.getStoredUser();
    const names = (stored?.roles ?? [])
      .map((r: any) => r?.role?.name)
      .filter(Boolean);
    if (names.length) return names;
    const primary = this.getRole();
    return primary ? [primary] : [];
  }

  hasRole(allowedRoles: string[]): boolean {
    // Superusers have access to everything
    if (this.isSuperUser()) return true;
    return this.getRoles().some((role) => allowedRoles.includes(role));
  }

  isSuperUser(): boolean {
    return this.getRoles().some((role) =>
      ['Admin', 'ADMIN', 'SUPER_ADMIN'].includes(role)
    );
  }

  /** Platform operator: may onboard workspaces, set prices, enter any tenant. */
  isSuperAdmin(): boolean {
    return this.getRoles().some((role) => String(role).toUpperCase() === 'SUPER_ADMIN');
  }

  // ── Workspace (tenant) context for SUPER_ADMIN ───────────────────────────

  private static readSelectedTenant(): SelectedTenant | null {
    try {
      const raw = localStorage.getItem(TENANT_KEY);
      return raw ? (JSON.parse(raw) as SelectedTenant) : null;
    } catch {
      return null;
    }
  }

  getSelectedTenant(): SelectedTenant | null {
    return this.selectedTenantSource.value;
  }

  getSelectedTenantId(): string | null {
    return this.selectedTenantSource.value?.id ?? null;
  }

  /**
   * Enter (or leave, with null) a workspace. The store/location choice of the
   * previous context is dropped because it belongs to another tenant; the
   * caller reloads the app so every screen re-reads its context.
   */
  setSelectedTenant(tenant: SelectedTenant | null): void {
    if (tenant) localStorage.setItem(TENANT_KEY, JSON.stringify(tenant));
    else localStorage.removeItem(TENANT_KEY);
    localStorage.removeItem('storeId');
    localStorage.removeItem('activeLocationId');
    localStorage.removeItem('PrimaryLocationName');
    this.locationSource.next(null);
    this.selectedTenantSource.next(tenant);
  }

  /**
   * Re-fetch /auth/me (which, for a super admin inside a workspace, lists that
   * workspace's stores) and bind the first store so screens have a location.
   */
  refreshUser(): Observable<any> {
    return this.getLoggedInUser().pipe(
      tap((user: any) => {
        localStorage.setItem('user', JSON.stringify(user));
        this.userSource.next(user);
        const access = user?.storeAccess ?? [];
        const activeId = localStorage.getItem('activeLocationId');
        const stillValid = access.some((a: any) => a.locationId === activeId);
        if (access.length && !stillValid) this.setActiveStore(access[0]);
      })
    );
  }

  isAdmin(): boolean {
    return this.isSuperUser();
  }

  /** Type (Warehouse/Store) of the location the user is working in. */
  getActiveLocationType(): string | null {
    const activeId = this.getActiveLocationId();
    if (!activeId) return null;
    const access = this.getStoreAccess().find(
      (a) => a.locationId === activeId
    );
    return access?.locationType ?? null;
  }

  getClientType(): string {
    const user = this.getStoredUser();
    return user?.clientType || '';
  }

  private getStoredUser(): any {
    const user = localStorage.getItem('user');
    return user ? JSON.parse(user) : null;
  }
}
